import { AHIcon, useLayoutContext, useTheme } from "arkh";
import { Platform } from "react-native";
import Animated, {
  SharedValue,
  useAnimatedReaction,
  useAnimatedStyle,
  useDerivedValue,
  useSharedValue,
  withTiming,
} from "react-native-reanimated";
import { Gesture, GestureDetector } from "react-native-gesture-handler";
import React, { PropsWithChildren } from "react";
import * as Haptic from "expo-haptics";
import { Positions } from "../../Positions";
import { scheduleOnRN } from "react-native-worklets";

type ReordrableListItemProps = PropsWithChildren<{
  positions: SharedValue<Positions>;
  activeItem: SharedValue<string | null>;
  id: string;
  height: number;
  onReorder(order: Positions): void;
  disabled?: boolean;
  hasFooter?: boolean;
  iconPosition?: "left" | "right";
}>;

const getIndex = (ty: number, height: number) => {
  "worklet";
  const y = Math.round(ty / height) * height;
  const index = Math.floor(Math.max(y, 0) / height);
  return index;
};

export function ReordrableListItem({
  activeItem,
  height,
  id,
  onReorder,
  positions,
  children,
  disabled,
  hasFooter,
  iconPosition: _iconPosition,
}: ReordrableListItemProps) {
  const layout = useLayoutContext();
  const theme = useTheme();
  const position = useDerivedValue<number | null>(
    () => positions.value[id] ?? null
  );
  const y = useSharedValue((position.value ?? 0) * height);
  useAnimatedReaction(
    () => position.value,
    (idx) => {
      if (idx !== null) {
        y.value = withTiming(idx * height, { duration: 300 });
      }
    },
    [height]
  );

  const animatedStyle = useAnimatedStyle(() => {
    const bottomItem =
      hasFooter !== true &&
      position.value === Object.keys(positions.value).length - 1;
    return {
      zIndex: activeItem.value === id ? 10 : 2,
      height,
      width: "100%",
      position: "absolute",
      top: 0,
      left: 0,
      borderBottomLeftRadius: withTiming(bottomItem ? layout.radius.large : 0),
      borderBottomRightRadius: withTiming(bottomItem ? layout.radius.large : 0),
      borderTopRightRadius: withTiming(
        position.value === 0 ? layout.radius.large : 0
      ),
      borderTopLeftRadius: withTiming(
        position.value === 0 ? layout.radius.large : 0
      ),
      borderBottomWidth: bottomItem || activeItem.value === id ? 0 : 1,
      borderBottomColor: theme["border-primary"],
      overflow: "hidden",
      backgroundColor: theme.surface,
      transform: [
        {
          translateY: y.value,
        },
      ],
    };
  });

  const ctx = useSharedValue({ y: 0 });
  const gesture = Gesture.Pan()
    .onStart(() => {
      ctx.value.y = (position.value ?? 0) * height;
      y.value = ctx.value.y;
      activeItem.value = id;
    })
    .onChange(({ translationY }) => {
      y.value = translationY + ctx.value.y;
      const newOrder = getIndex(y.value, height);
      const oldOrder = position.value;
      if (oldOrder !== null && newOrder !== oldOrder) {
        const idToSwap = Object.keys(positions.value).find(
          (key) => positions.value[key] === newOrder
        );
        if (idToSwap) {
          const newPositions = Object.assign({}, positions.value);
          newPositions[id] = newOrder;
          newPositions[idToSwap] = oldOrder;
          positions.value = newPositions;
        }
      }
    })
    .onEnd(() => {
      const newPosition = positions.value[id];
      y.value = withTiming(newPosition * height, undefined, () => {
        activeItem.value = null;
        scheduleOnRN(onReorder, positions.value);
      });
      if (Platform.OS === "ios") {
        scheduleOnRN(Haptic.impactAsync, Haptic.ImpactFeedbackStyle.Light);
      }
    })
    .hitSlop({ left: 90 })
    .activeOffsetY(0)
    .minDistance(0)
    .enabled(!disabled);

  const iconPosition = _iconPosition === "left" ? "left" : "right";
  return (
    <Animated.View style={animatedStyle}>
      {children}
      <GestureDetector gesture={gesture}>
        <Animated.View
          style={{
            position: "absolute",
            [iconPosition]: 0,
            top: 0,
            bottom: 0,
            height: "100%",
            alignItems: "center",
            justifyContent: "center",
            paddingLeft: layout.space.medium,
            paddingRight: layout.space.medium,
          }}
        >
          <AHIcon type="drag" color="text-secondary" />
        </Animated.View>
      </GestureDetector>
    </Animated.View>
  );
}

// export const ReordrableListItemRelativeIndicator = React.memo(() => (
//   <AHIcon type="drag" />
// ));

// export const ReordrableListItemFloatingIndicator = React.memo(() => {
//   const layout = useLayoutContext();
//   return (
//     <Animated.View
//       style={{
//         position: "absolute",
//         right: 0,
//         top: 0,
//         bottom: 0,
//         height: "100%",
//         alignItems: "center",
//         justifyContent: "center",
//         paddingRight: layout.space.medium,
//       }}
//     >
//       <AHIcon type="drag" />
//     </Animated.View>
//   );
// });
